import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../configureStore';
import { FormConfig } from '../components/organisms/TaxReturnWebforms/types/formConfig';
import { getExtractionConfig } from '../redux/reducer/configs';
import LOCAL_DEV_CONFIGS, { USE_LOCAL_CONFIGS } from './localDevConfigs';

/**
 * Hook to fetch form configuration from the backend.
 * Caches configs in Redux to avoid redundant API calls.
 *
 * @param formType - The form type identifier (e.g., '1040', 'schedule_c')
 * @param taxYear - Optional tax year (e.g., 2024). Uses 'default' if not provided.
 * @returns Object containing config, loading state, and any error
 */
export function useFormConfig(formType: string, taxYear?: number): {
    config: FormConfig | null;
    loading: boolean;
    error: string | null;
} {
    const dispatch = useDispatch<AppDispatch>();

    // Build config name: {formType}-{year} or {formType}-default
    const configName = taxYear ? `${formType}-${taxYear}` : `${formType}-default`;

    // Select from Redux state
    const config = useSelector<RootState, FormConfig | undefined>(
        ({ configs }) => configs.extractionConfigs[configName],
    );
    const loading = useSelector<RootState, boolean>(
        ({ configs }) => configs.extractionConfigsLoading[configName] ?? false,
    );
    const error = useSelector<RootState, string | undefined>(
        ({ configs }) => configs.extractionConfigsError[configName],
    );

    const localConfig = USE_LOCAL_CONFIGS
        ? LOCAL_DEV_CONFIGS[configName] ?? LOCAL_DEV_CONFIGS[`${formType}-default`]
        : undefined;

    // Fetch config if not in cache and no error (don't retry on error)
    useEffect(() => {
        if (!localConfig && !config && !loading && !error) {
            dispatch(getExtractionConfig({
                input: { configName },
            }));
        }
    }, [dispatch, configName, localConfig, config, loading, error]);

    if (localConfig) {
        return { config: localConfig, loading: false, error: null };
    }

    return {
        config: config ?? null,
        loading,
        error: error ?? null,
    };
}
